<template>
  <div v-if="current_user">
    <!-- Mobile view with tabs -->
    <v-card v-if="isMobile" class="content-card mobile-card">
      <v-tabs
        v-model="tabs"
        bg-color="primary-lighten-4"
        slider-color="primary-darken-2"
        grow
        class="tab-nav"
        density="compact"
        @update:model-value="handleTabChange"
      >
        <!-- Day report lines tab -->
        <v-tab value="veldwerk" class="tab-icon">
          <v-icon>mdi-clipboard-text</v-icon>
        </v-tab>

        <!-- DGs tab -->
        <v-tab value="dgs" class="tab-icon">
          <v-icon>mdi-account-group</v-icon>
        </v-tab>

        <!-- Comments tab -->
        <v-tab value="comments" class="tab-icon">
          <v-icon>mdi-comment-multiple</v-icon>
        </v-tab>

        <!-- Attachments tab -->
        <v-tab value="info" class="tab-icon">
          <v-icon>mdi-paperclip</v-icon>
        </v-tab>

        <!-- Deadlines tab -->
        <v-tab value="taken" class="tab-icon">
          <v-icon>mdi-calendar-check</v-icon>
          <v-badge
            v-if="past_deadlines > 0"
            color="error"
            dot
            location="top end"
            offset-x="2"
            offset-y="2"
          ></v-badge>
        </v-tab>
      </v-tabs>

      <v-window v-model="tabs">
        <!-- Day report lines tab content -->
        <v-window-item value="veldwerk">
          <div v-if="day_report" class="pa-1">
            <DayReportContent
              :day_report="day_report"
              :current_active="true"
              :can-edit="true"
              @update:day_report="handleDayReportSaved"
            />
          </div>
          <div v-else class="pa-2 text-center">
            <div class="content-card pa-3 mb-3">
              <div class="d-flex flex-column align-center">
                <v-icon
                  size="48"
                  color="grey-lighten-1"
                  icon="mdi-clipboard-text-outline"
                  class="mb-3"
                ></v-icon>
                <h3 class="text-h6 mb-1">Geen actief verslag</h3>
                <p class="text-body-2 text-medium-emphasis mb-3">
                  Je hebt momenteel geen actief dagverslag. Maak er een aan om
                  je werk te registreren.
                </p>
                <v-btn
                  color="primary"
                  @click="
                    formsStore.edit({
                      type: 'day_report',
                      item: new_day_report,
                    })
                  "
                >
                  Nieuw verslag maken
                </v-btn>
              </div>
            </div>
          </div>
        </v-window-item>

        <!-- DGs tab content -->
        <v-window-item value="dgs">
          <div class="pa-1">
            <div v-if="accounts && accounts.length > 0">
              <AccountBox
                v-for="account in accounts"
                :key="`account-${account.id}-${day_report.id}`"
                :account="account"
                :day_report="day_report"
                class="mb-2"
              />
            </div>
            <v-alert
              v-else
              type="info"
              variant="tonal"
              icon="mdi-information"
              class="mt-2"
              density="compact"
            >
              Je hebt nog geen rapporten voor DGs aangemaakt binnen dit
              veldwerkverslag.
            </v-alert>
          </div>
        </v-window-item>

        <!-- Comments tab content -->
        <v-window-item value="comments">
          <div class="pa-1">
            <CommentBox
              v-if="current_user.active_day_report_id && day_report"
              :id="day_report.id"
              type="DayReport"
            />
            <v-alert
              v-else
              type="info"
              variant="tonal"
              icon="mdi-information"
              class="pa-4"
              density="compact"
            >
              Geen actief verslag om reacties op te plaatsen.
            </v-alert>
          </div>
        </v-window-item>

        <!-- Attachments tab content -->
        <v-window-item value="info">
          <div class="pa-1">
            <AttachmentsBox
              v-if="current_user.active_day_report_id && day_report"
              record-type="DayReport"
              :record-id="day_report.id"
              :item="day_report"
            />
            <v-alert
              v-else
              type="info"
              variant="tonal"
              icon="mdi-information"
              class="pa-4"
              density="compact"
            >
              Geen actief verslag om bijlages aan toe te voegen.
            </v-alert>
          </div>
        </v-window-item>

        <!-- Deadlines tab content -->
        <v-window-item value="taken">
          <div class="pa-1">
            <v-card class="mb-2 content-card" variant="outlined">
              <v-card-title
                class="d-flex align-center justify-space-between bg-green-lighten-5 mobile-card-title"
              >
                <div class="d-flex align-center">
                  <v-icon start color="green-darken-1" class="mr-1"
                    >mdi-calendar-check</v-icon
                  >
                  Mijn taken
                  <v-chip
                    v-if="past_deadlines > 0"
                    color="error-lighten-1"
                    size="x-small"
                    class="ml-1"
                  >
                    {{ past_deadlines }}
                  </v-chip>
                </div>
                <v-btn
                  v-if="current_user"
                  variant="text"
                  color="green-darken-1"
                  size="small"
                  prepend-icon="mdi-plus"
                  @click="createDeadlineAssigned"
                >
                  Nieuwe taak
                </v-btn>
              </v-card-title>
              <v-card-text class="pa-0">
                <DeadlineList
                  :deadlines="deadlines"
                  :show_account="true"
                  :hide_add="true"
                />
              </v-card-text>
            </v-card>

            <v-card variant="outlined" class="content-card">
              <v-card-title
                class="d-flex align-center justify-space-between bg-blue-lighten-5 mobile-card-title"
              >
                <div class="d-flex align-center">
                  <v-icon start color="blue-darken-1" class="mr-1"
                    >mdi-account-group</v-icon
                  >
                  Andere taken
                </div>
                <v-btn
                  variant="text"
                  color="blue-darken-1"
                  size="small"
                  prepend-icon="mdi-plus"
                  @click="createDeadlineUnassigned"
                >
                  Nieuwe taak
                </v-btn>
              </v-card-title>
              <v-card-text class="pa-0">
                <DeadlineList
                  :deadlines="other_deadlines"
                  :show_account="true"
                  :hide_add="true"
                />
              </v-card-text>
            </v-card>
          </div>
        </v-window-item>
      </v-window>
    </v-card>

    <!-- Desktop view with columns -->
    <v-container v-else class="px-4 py-0">
      <v-row>
        <!-- Left column: Day report + DGs -->
        <v-col cols="12" lg="7">
          <!-- Day Report -->

          <!-- Current day report or day report form -->
          <div v-if="!current_user.active_day_report_id" class="py-2">
            <DayReportOpenPlanned
              :next_day_report="next_day_report"
              class="mb-4"
            />
            <DayReportForm
              :day_report="new_day_report"
              :hide_user="true"
              @saved="handleDayReportSaved"
            />
          </div>

          <DayReportContent
            v-else
            :day_report="day_report"
            :current_active="true"
            :can-edit="true"
            class="mb-4"
          />

          <!-- DGs/Account Boxes -->
          <v-card
            v-if="current_user.active_day_report_id && day_report"
            class="mb-4 content-card"
          >
            <v-card-title class="d-flex align-center bg-blue-lighten-5">
              <v-icon start color="blue-darken-1" class="mr-2"
                >mdi-account-group</v-icon
              >
              DGs
            </v-card-title>
            <v-card-text>
              <div v-if="accounts && accounts.length > 0" class="py-2">
                <AccountBox
                  v-for="account in accounts"
                  :key="`account-${account.id}-${day_report.id}`"
                  :account="account"
                  :day_report="day_report"
                  class="mb-3"
                />
              </div>
              <v-alert
                v-else
                type="info"
                variant="tonal"
                icon="mdi-information"
                class="mt-2"
              >
                Je hebt nog geen rapporten voor DGs aangemaakt binnen dit
                veldwerkverslag.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Right column: Attachments + Comments + Deadlines -->
        <v-col cols="12" lg="5">
          <!-- Attachments -->
          <AttachmentsBox
            v-if="current_user.active_day_report_id && day_report"
            record-type="DayReport"
            :record-id="day_report.id"
            :item="day_report"
            class="mb-4"
          />

          <!-- Comments (Only visible on desktop) -->
          <CommentBox
            v-if="current_user.active_day_report_id && day_report && !isMobile"
            :id="day_report.id"
            type="DayReport"
            class="mb-4"
          />

          <!-- Deadlines - My tasks -->
          <v-card class="mb-4 content-card">
            <v-card-title
              class="d-flex align-center justify-space-between bg-green-lighten-5"
            >
              <div class="d-flex align-center">
                <v-icon start color="green-darken-1" class="mr-2"
                  >mdi-calendar-check</v-icon
                >
                Mijn taken
                <v-chip
                  v-if="past_deadlines > 0"
                  color="error-lighten-1"
                  size="small"
                  class="ml-2"
                >
                  {{ past_deadlines }}
                </v-chip>
              </div>
              <v-btn
                v-if="current_user"
                variant="text"
                color="green-darken-1"
                size="small"
                prepend-icon="mdi-plus"
                @click="createDeadlineAssigned"
              >
                Nieuwe taak
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <DeadlineList
                :deadlines="deadlines"
                :show_account="true"
                :hide_add="true"
              />
            </v-card-text>
          </v-card>

          <!-- Deadlines - Other tasks -->
          <v-card class="content-card">
            <v-card-title
              class="d-flex align-center justify-space-between bg-blue-lighten-5"
            >
              <div class="d-flex align-center">
                <v-icon start color="blue-darken-1" class="mr-2"
                  >mdi-account-group</v-icon
                >
                Andere taken
              </div>
              <v-btn
                variant="text"
                color="blue-darken-1"
                size="small"
                prepend-icon="mdi-plus"
                @click="createDeadlineUnassigned"
              >
                Nieuwe taak
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-0">
              <DeadlineList
                :deadlines="other_deadlines"
                :show_account="true"
                :hide_add="true"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import DayReportContent from "./DayReportContent.vue";
import DeadlineList from "../deadlines/DeadlineList.vue";
import DayReportForm from "./DayReportForm.vue";
import AccountBox from "../accounts/AccountBox.vue";
import DayReportOpenPlanned from "./DayReportOpenPlanned.vue";
import AttachmentsBox from "../attachments/AttachmentsBox.vue";
import CommentBox from "../comments/CommentBox.vue";
import { useAuthStoreRefactor } from "../../stores/authStoreRefactor";
import { useDayReportsStoreRefactor } from "../../stores/dayReportsStoreRefactor";
import { useReportsStoreRefactor } from "../../stores/reportsStoreRefactor";
import { useDeadlinesStoreRefactor } from "../../stores/deadlinesStoreRefactor";
import { useFormsStore } from "../../stores/formsStore";
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import moment from "moment";
import { useRoute, useRouter } from "vue-router";
import { useAccountsStoreRefactor } from "../../stores/accountsStoreRefactor";
import { useDashboardCoordinationStore } from "../../stores/dashboardCoordinationStore";
import { useContextMenuStore } from "../../stores/contextMenuStore";
import { useDayReportRefresh } from "../../composables/useDayReportRefresh";

function getUnique(arr, comp) {
  const unique = arr
    .map((e) => e[comp])
    .map((e, i, final) => final.indexOf(e) === i && i)
    .filter((e) => arr[e])
    .map((e) => arr[e]);

  return unique;
}

export default {
  name: "DayReportOverview",
  components: {
    DayReportContent,
    AccountBox,
    DayReportForm,
    DeadlineList,
    DayReportOpenPlanned,
    AttachmentsBox,
    CommentBox,
  },
  setup() {
    const authStore = useAuthStoreRefactor();
    const dayReportsStore = useDayReportsStoreRefactor();
    const reportsStore = useReportsStoreRefactor();
    const deadlinesStore = useDeadlinesStoreRefactor();
    const formsStore = useFormsStore();
    const route = useRoute();
    const router = useRouter();
    const accountsStore = useAccountsStoreRefactor();
    const dashboardCoordinationStore = useDashboardCoordinationStore();
    const contextMenuStore = useContextMenuStore();

    // Data
    const isMobile = ref(false);

    // Computed
    const current_user = computed(() => authStore.user);

    // Past deadlines count - replaces the Vuex ORM query
    const past_deadlines = computed(() => {
      if (!current_user.value) return 0;

      return deadlinesStore.openDeadlines.filter(
        (d) =>
          d.user_id === current_user.value.id && moment().isAfter(d.deadline),
      ).length;
    });

    // User deadlines - replaces the Vuex ORM query
    const deadlines = computed(() => {
      if (!current_user.value) return [];
      return deadlinesStore.getOpenByUser(current_user.value.id);
    });

    // Other deadlines - replaces the Vuex ORM query
    const other_deadlines = computed(() => {
      if (!current_user.value) return [];
      return deadlinesStore.getOpenNotByUser(current_user.value.id);
    });

    // Get the active day report using dashboard coordination
    const day_report = computed(() => {
      if (!current_user.value || !current_user.value.active_day_report_id) {
        console.log("DayReportOverview - No active day report found");
        return null;
      }

      const reportId = current_user.value.active_day_report_id;
      const report = dayReportsStore.getDayReportById(reportId);

      console.log(
        `DayReportOverview - Looking for day report ID ${reportId} in store`,
      );

      if (report) {
        console.log("DayReportOverview - Using day_report from store");
        return report;
      }

      return null;
    });

    // Use the day report refresh composable (after day_report is defined)
    const { refreshDayReport } = useDayReportRefresh(day_report);

    const next_day_report = computed(() => {
      if (!current_user.value || !current_user.value.next_day_report_id)
        return null;

      return {
        id: current_user.value.next_day_report_id,
      };
    });

    const new_day_report = computed(() => {
      if (!current_user.value) return {};
      return {
        date: moment().format("YYYY-MM-DD"),
        user_id: current_user.value.id,
      };
    });

    // Get accounts that have reports for this day_report using coordinated data
    const accounts = computed(() => {
      if (!day_report.value?.id) return [];

      // Get all reports for this day_report from the coordinated data
      const dayReportReports = reportsStore.data.filter(
        (report) =>
          report.day_report_id == day_report.value.id ||
          String(report.day_report_id) === String(day_report.value.id),
      );

      // Get unique account IDs from these reports
      const accountIds = [
        ...new Set(
          dayReportReports.map((report) => report.account_id).filter(Boolean),
        ),
      ];

      // Get the actual account objects from the accounts store
      const accountsFromStore = accountIds
        .map((accountId) => accountsStore.getById(accountId))
        .filter(Boolean); // Remove any null accounts

      console.log(
        `DayReportOverview: Found ${accountsFromStore.length} accounts with reports for day_report ${day_report.value.id}`,
      );

      // Fallback: if no accounts found in store but we have embedded accounts in day_report, use those
      if (
        accountsFromStore.length === 0 &&
        day_report.value?.accounts?.length > 0
      ) {
        console.log(
          "DayReportOverview: Falling back to embedded accounts array",
        );
        return getUnique(day_report.value.accounts, "id");
      }

      return accountsFromStore;
    });

    // Methods
    const fetchData = async () => {
      try {
        console.log("DayReportOverview: Fetching essential data");

        // Get current user info first to ensure we have the active_day_report_id
        await authStore.getCurrentUser();

        // Use dashboard coordination to load all related data
        if (authStore.user?.active_day_report_id) {
          console.log(
            "DayReportOverview: Coordinating dashboard load for active day report:",
            authStore.user.active_day_report_id,
          );

          try {
            await dashboardCoordinationStore.coordinateDashboardLoad(
              authStore.user.active_day_report_id,
              accountsStore,
              reportsStore,
              dayReportsStore,
            );

            console.log(
              "DayReportOverview: Dashboard coordination completed successfully",
            );
          } catch (error) {
            console.error(
              "DayReportOverview: Error during dashboard coordination:",
              error,
            );
            // Don't fail the whole operation if coordination fails
          }
        } else {
          console.log("DayReportOverview: No active day report found for user");
        }

        // Load deadlines
        await deadlinesStore.getAll();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const onResize = () => {
      const big_sizes = ["xl", "lg"];
      // For Vue 3, we need to check window sizes directly
      isMobile.value = !big_sizes.includes(
        window.innerWidth > 1200 ? "xl" : window.innerWidth > 992 ? "lg" : "md",
      );
    };

    // Tab management
    const tabs = ref("veldwerk");

    // Function to handle tab change
    const handleTabChange = (newTab) => {
      console.log(`DayReportOverview: Tab changed to ${newTab}`);

      // Update URL query parameter to reflect current tab
      const currentQuery = { ...route.query };
      currentQuery.tab = newTab;

      // Use router.replace to update URL without navigation
      router
        .replace({
          path: route.path,
          query: currentQuery,
        })
        .catch((err) => {
          console.error("Navigation error:", err);
        });
    };

    // Set initial tab based on query parameters
    watch(
      () => route.query.tab,
      (newTab) => {
        if (newTab) {
          console.log(
            `DayReportOverview: Setting tab from URL query: ${newTab}`,
          );
          tabs.value = newTab;
        }
      },
      { immediate: true },
    );

    // Also switch to "taken" tab if deadline parameter is present
    watch(
      () => route.query.deadline,
      (deadlineId) => {
        if (deadlineId) {
          console.log(
            `DayReportOverview: Deadline ID detected in URL: ${deadlineId}, switching to taken tab`,
          );
          tabs.value = "taken";

          // Ensure that after switching tabs, the UI is updated before scrolling
          setTimeout(() => {
            const element = document.getElementById(`d${deadlineId}`);
            if (element) {
              console.log(
                `DayReportOverview: Scrolling to deadline element d${deadlineId}`,
              );
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
              console.log(
                `DayReportOverview: Deadline element not found for ID ${deadlineId}`,
              );
            }
          }, 500);
        }
      },
      { immediate: true },
    );

    // Context menu definition
    const context_menu = [
      {
        name: "Vernieuwen",
        action: refreshDayReport,
        icon: "mdi-refresh",
      },
    ];

    // Detect screen size for mobile view
    onMounted(async () => {
      console.log("DayReportOverview: Component mounted");
      console.log(
        "DayReportOverview: Adding context menu options:",
        context_menu,
      );

      // Use nextTick to ensure the context menu is set after any route-based resets
      await nextTick();
      contextMenuStore.addOption(context_menu);

      // Get tab from URL if available
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get("tab");
      if (tabParam) {
        tabs.value = tabParam;
      }

      // Only fetch data if the user is authenticated (token exists)
      if (authStore.token) {
        fetchData();
      }

      onResize();
      window.addEventListener("resize", onResize, { passive: true });
    });

    onUnmounted(() => {
      console.log(
        "DayReportOverview: Component unmounted, clearing context menu and cancelling requests",
      );
      window.removeEventListener("resize", onResize);
      contextMenuStore.reset(); // Clear context menu when component unmounts

      // 🔒 RACE CONDITION FIX: Cancel any pending account fetch requests
      // This prevents dashboard account loading from interfering with the account list
      // when the user navigates away before loading completes
      if (accountsStore.cancelAllRequests) {
        console.log("DayReportOverview: Cancelling pending account requests");
        accountsStore.cancelAllRequests();
      }
    });

    const handleDayReportSaved = async (createdDayReport) => {
      console.log("Day report saved:", createdDayReport);

      // Force refresh the current user data to get the updated active_day_report_id
      // Use forceRefresh=true to ensure we get fresh data from the backend
      await authStore.getCurrentUser(true);

      console.log(
        "DayReportOverview: User refreshed, active_day_report_id:",
        authStore.user?.active_day_report_id,
      );

      // Wait for Vue to update the DOM based on the new user state
      await nextTick();

      // Use dashboard coordination to reload all related data
      if (authStore.user?.active_day_report_id) {
        console.log(
          "DayReportOverview: Coordinating dashboard reload after day report save:",
          authStore.user.active_day_report_id,
        );

        try {
          await dashboardCoordinationStore.coordinateDashboardLoad(
            authStore.user.active_day_report_id,
            accountsStore,
            reportsStore,
            dayReportsStore,
            { forceRefresh: true }, // Force refresh to ensure fresh dashboard data
          );

          console.log(
            "DayReportOverview: Dashboard coordination after save completed successfully",
          );

          // Wait for dashboard render to complete
          await nextTick();
        } catch (error) {
          console.error(
            "DayReportOverview: Error during dashboard coordination after save:",
            error,
          );
        }
      } else {
        console.log(
          "DayReportOverview: No active day report after save - keeping form view",
        );
      }

      // Refresh deadlines
      await deadlinesStore.getAll();
    };

    // Watch for when user data becomes available for the first time
    watch(
      () => current_user.value,
      async (newUser, oldUser) => {
        // Only trigger on the initial user load (from null to user object)
        if (!oldUser && newUser && newUser.active_day_report_id) {
          console.log(
            "DayReportOverview: User loaded with active day report, coordinating dashboard load",
          );

          try {
            await dashboardCoordinationStore.coordinateDashboardLoad(
              newUser.active_day_report_id,
              accountsStore,
              reportsStore,
              dayReportsStore,
            );
          } catch (error) {
            console.error(
              "DayReportOverview: Error during initial dashboard coordination:",
              error,
            );
          }
        }
      },
      { immediate: false },
    );

    // Watch for changes in active day report ID and coordinate dashboard load
    watch(
      () => current_user.value?.active_day_report_id,
      async (newActiveDayReportId, oldActiveDayReportId) => {
        console.log(
          `DayReportOverview: Active day report ID changed from ${oldActiveDayReportId} to ${newActiveDayReportId}`,
        );

        if (
          newActiveDayReportId &&
          newActiveDayReportId !== oldActiveDayReportId
        ) {
          console.log(
            `DayReportOverview: Coordinating dashboard load for new active day report ${newActiveDayReportId}`,
          );

          try {
            await dashboardCoordinationStore.coordinateDashboardLoad(
              newActiveDayReportId,
              accountsStore,
              reportsStore,
              dayReportsStore,
            );
          } catch (error) {
            console.error(
              "DayReportOverview: Error during dashboard coordination for new active day report:",
              error,
            );
          }
        }
      },
      { immediate: true },
    );

    // Methods to create new deadlines
    function createDeadlineAssigned() {
      formsStore.edit({
        type: "deadline",
        item: {
          deadline: moment().add(1, "day").format("YYYY-MM-DD"),
          user_id: current_user.value?.id || null,
          account_id: null,
        },
        options: {},
      });
    }

    function createDeadlineUnassigned() {
      formsStore.edit({
        type: "deadline",
        item: {
          deadline: moment().add(1, "day").format("YYYY-MM-DD"),
          user_id: null,
          account_id: null,
        },
        options: {},
      });
    }

    return {
      // Data
      isMobile,

      // Computed
      current_user,
      past_deadlines,
      deadlines,
      other_deadlines,
      day_report,
      next_day_report,
      new_day_report,
      accounts,
      tabs,

      // Methods
      fetchData,
      onResize,
      handleTabChange,
      handleDayReportSaved,
      createDeadlineAssigned,
      createDeadlineUnassigned,

      // Stores for template access
      authStore,
      dayReportsStore,
      deadlinesStore,
      formsStore,
      dashboardCoordinationStore,

      // Make moment available globally
      moment,
    };
  },
};
</script>

<style scoped>
.content-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.v-card-title {
  font-size: 1.25rem;
  padding: 12px 16px;
}

.v-window {
  background-color: white;
}

.v-window-item {
  padding: 0;
}

.v-window-item > div {
  margin-top: 12px;
}

.mobile-card {
  padding: 0;
  margin: 0;
}

.mobile-card-title {
  padding: 8px 12px;
}

/* Tab navigation styling */
.tab-nav {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Icon-only tab styling */
.tab-icon {
  min-width: 0 !important;
  width: auto !important;
  padding: 0 !important;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.tab-icon:hover,
.tab-icon.v-tab--selected {
  opacity: 1;
}

.tab-icon .v-icon {
  margin: 0 !important;
  font-size: 24px !important;
}

/* Make sure the tabs take up equal space */
.v-tabs--density-compact {
  height: 56px;
}

.v-tabs--density-compact .v-tab {
  height: 100%;
}
</style>
